import { useState } from 'react';
import { Building2, Check, X, Plus, Loader2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { OrganizationMembership } from '@/api/auth/missionImpactAuthAPI.schemas';
import {
  usePostMeOrganizationsInvitationsAccept,
  usePostMeOrganizationsInvitationsDecline,
} from '@/api/auth/self/self';
import { NewOrganizationDialog } from '@/components/orgs/NewOrganizationDialog';
import { OrganizationForm } from '@/components/orgs/OrganizationForm';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';

interface OrganizationStepProps {
  /** Organizations the user is already a member of */
  activeOrgs: OrganizationMembership[];
  /** Pending invitations */
  pendingInvites: OrganizationMembership[];
  /** Called when orgs change (invite accepted/declined or org created) */
  onOrgsChanged: () => void;
  onNext: () => void;
  /** Called when user wants to go back (optional - hides back button if not provided) */
  onBack?: () => void;
}

/**
 * Step for managing organization membership during onboarding.
 * Shows active memberships, pending invites, and allows creating new orgs.
 * If user has no orgs and no pending invites, shows the create form directly.
 */
export function OrganizationStep({
  activeOrgs,
  pendingInvites,
  onOrgsChanged,
  onNext,
  onBack,
}: OrganizationStepProps) {
  const { t, language } = useLanguage();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null);

  const acceptInvite = usePostMeOrganizationsInvitationsAccept();
  const declineInvite = usePostMeOrganizationsInvitationsDecline();

  const hasActiveOrg = activeOrgs.length > 0;
  const hasPendingInvites = pendingInvites.length > 0;
  const showCreateFormDirectly = !hasActiveOrg && !hasPendingInvites;

  /**
   * Format expiration date based on language
   */
  const formatExpiration = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const locale = language === 'fr' ? 'fr-FR' : language === 'de' ? 'de-DE' : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Handle accepting an invitation
   */
  const handleAcceptInvite = async (invite: OrganizationMembership) => {
    setProcessingInviteId(invite.orgId);
    try {
      await acceptInvite.mutateAsync({ orgId: invite.orgId });
      toast.success(`Joined ${invite.orgName}`);
      onOrgsChanged();
    } catch {
      toast.error(t.userOnboardingWizard.organizationStep.acceptError);
    } finally {
      setProcessingInviteId(null);
    }
  };

  /**
   * Handle declining an invitation
   */
  const handleDeclineInvite = async (invite: OrganizationMembership) => {
    setProcessingInviteId(invite.orgId);
    try {
      await declineInvite.mutateAsync({ orgId: invite.orgId });
      onOrgsChanged();
    } catch {
      toast.error(t.userOnboardingWizard.organizationStep.declineError);
    } finally {
      setProcessingInviteId(null);
    }
  };

  /**
   * Handle org creation success - refresh orgs and auto-advance
   */
  const handleOrgCreated = () => {
    onOrgsChanged();
    onNext();
  };

  /**
   * Handle org creation from dialog - refresh orgs only (user must click Continue)
   */
  const handleOrgCreatedFromDialog = () => {
    onOrgsChanged();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasActiveOrg) {
      onNext();
    }
  };

  // Case A: No orgs and no pending invites - show create form directly
  if (showCreateFormDirectly) {
    return (
      <Card className="h-[480px] flex flex-col">
        <CardHeader>
          <CardTitle>{t.userOnboardingWizard.organizationStep.title}</CardTitle>
          <CardDescription>
            {t.userOnboardingWizard.organizationStep.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <OrganizationForm
            onSuccess={handleOrgCreated}
            onCancel={onBack}
            submitLabel={t.userOnboardingWizard.themeStep.continue}
            cancelLabel={t.userOnboardingWizard.organizationStep.back}
          />
        </CardContent>
      </Card>
    );
  }

  // Case B: Has orgs or pending invites - show existing UI with lists and dialog
  return (
    <>
      <Card className="h-[480px] flex flex-col">
        <CardHeader>
          <CardTitle>{t.userOnboardingWizard.organizationStep.title}</CardTitle>
          <CardDescription>
            {t.userOnboardingWizard.organizationStep.description}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
          <CardContent className="space-y-4 flex-1 overflow-y-auto">
            {/* Active Memberships */}
            {activeOrgs.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">
                  {t.userOnboardingWizard.organizationStep.activeMemberships}
                </h4>
                <div className="space-y-2">
                  {activeOrgs.map((org) => (
                    <div
                      key={org.orgId}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30"
                    >
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="font-medium">{org.orgName}</p>
                        <p className="text-sm text-muted-foreground">
                          {t.userOnboardingWizard.organizationStep.role}:{' '}
                          {t.organizationMembers.roles[org.role]}
                        </p>
                      </div>
                      <Check className="h-5 w-5 text-primary" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Invitations */}
            {pendingInvites.length > 0 && (
              <div className="space-y-2">
                {activeOrgs.length > 0 && <Separator />}
                <h4 className="text-sm font-medium">
                  {t.userOnboardingWizard.organizationStep.pendingInvites}
                </h4>
                <div className="space-y-2">
                  {pendingInvites.map((invite) => {
                    const isProcessing = processingInviteId === invite.orgId;
                    return (
                      <div
                        key={invite.orgId}
                        className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background"
                      >
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{invite.orgName}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>
                              {t.userOnboardingWizard.organizationStep.role}:{' '}
                              {t.organizationMembers.roles[invite.role]}
                            </span>
                            {invite.invitationExpiresUtc && (
                              <>
                                <span>•</span>
                                <span>
                                  {t.userOnboardingWizard.organizationStep.expires}:{' '}
                                  {formatExpiration(invite.invitationExpiresUtc)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => { void handleDeclineInvite(invite); }}
                            disabled={isProcessing}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            {isProcessing ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => { void handleAcceptInvite(invite); }}
                            disabled={isProcessing}
                          >
                            {isProcessing ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              t.userOnboardingWizard.organizationStep.accept
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Create Organization */}
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => { setCreateDialogOpen(true); }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t.userOnboardingWizard.organizationStep.createNew}
              </Button>
            </div>
          </CardContent>

          <CardFooter className={`flex pt-4 ${onBack ? 'justify-between' : 'justify-end'}`}>
            {onBack && (
              <Button type="button" variant="outline" onClick={onBack}>
                {t.userOnboardingWizard.organizationStep.back}
              </Button>
            )}
            <Button type="submit" disabled={!hasActiveOrg}>
              {t.userOnboardingWizard.themeStep.continue}
            </Button>
          </CardFooter>
        </form>
      </Card>

      <NewOrganizationDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleOrgCreatedFromDialog}
      />
    </>
  );
}
